import { useState, useEffect, useRef } from "react";
import { Outlet, Link } from "react-router-dom";
import { PortalProvider } from "../context/PortalContext";
import { useAuth } from "../context/AuthContext";
import { AvatarCropModal } from "./AvatarCropModal";
import Grainient from "./ui/Grainient";
import styles from "./Shell.module.css";

function ShellInner() {
  const { user, refreshUser } = useAuth();
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [serverOk, setServerOk] = useState<boolean | null>(null);

  useEffect(() => {
    function poll() {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      fetch("/health", { signal: controller.signal })
        .then((r) => {
          setServerOk(r.ok);
        })
        .catch(() => {
          setServerOk(false);
        })
        .finally(() => {
          clearTimeout(timeout);
        });
    }
    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!avatarOpen) return;
    function handleClick(e: MouseEvent) {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [avatarOpen]);

  function handleAvatarFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarOpen(false);
    setCropFile(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleCropSave(dataUrl: string) {
    setUploading(true);
    try {
      const blob = await fetch(dataUrl).then((r) => r.blob());
      const form = new FormData();
      form.append("avatar", blob, "avatar.jpg");
      const res = await fetch("/api/me/avatar", { method: "POST", body: form });
      if (res.ok) await refreshUser();
    } finally {
      setUploading(false);
      setCropFile(null);
    }
  }

  function handleCropCancel() {
    setCropFile(null);
  }

  return (
    <div className={styles.shell}>
      <div className={styles.grainientBg}>
        <Grainient
          color1="#661a29"
          color2="#cb163a"
          color3="#382323"
          timeSpeed={0.25}
          colorBalance={0}
          warpStrength={1}
          warpFrequency={5}
          warpSpeed={2}
          warpAmplitude={50}
          blendAngle={0}
          blendSoftness={0.05}
          rotationAmount={500}
          noiseScale={2}
          grainAmount={0.13}
          grainScale={2}
          grainAnimated={false}
          contrast={1.1}
          gamma={0.9}
          saturation={0.85}
          centerX={0}
          centerY={0}
          zoom={0.9}
        />
      </div>
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <Link to="/" className={styles.wordmarkLink}>
            <span className={styles.wordmark}>
              <svg
                className={styles.wordmarkCrown}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M19.6872 14.0931L19.8706 12.3884C19.9684 11.4789 20.033 10.8783 19.9823 10.4999L20 10.5C20.8284 10.5 21.5 9.82843 21.5 9C21.5 8.17157 20.8284 7.5 20 7.5C19.1716 7.5 18.5 8.17157 18.5 9C18.5 9.37466 18.6374 9.71724 18.8645 9.98013C18.5384 10.1814 18.1122 10.606 17.4705 11.2451L17.4705 11.2451C16.9762 11.7375 16.729 11.9837 16.4533 12.0219C16.3005 12.043 16.1449 12.0213 16.0038 11.9592C15.7492 11.847 15.5794 11.5427 15.2399 10.934L13.4505 7.7254C13.241 7.34987 13.0657 7.03557 12.9077 6.78265C13.556 6.45187 14 5.77778 14 5C14 3.89543 13.1046 3 12 3C10.8954 3 10 3.89543 10 5C10 5.77778 10.444 6.45187 11.0923 6.78265C10.9343 7.03559 10.759 7.34984 10.5495 7.7254L8.76006 10.934C8.42056 11.5427 8.25081 11.847 7.99621 11.9592C7.85514 12.0213 7.69947 12.043 7.5467 12.0219C7.27097 11.9837 7.02381 11.7375 6.5295 11.2451C5.88787 10.606 5.46156 10.1814 5.13553 9.98012C5.36264 9.71724 5.5 9.37466 5.5 9C5.5 8.17157 4.82843 7.5 4 7.5C3.17157 7.5 2.5 8.17157 2.5 9C2.5 9.82843 3.17157 10.5 4 10.5L4.01771 10.4999C3.96702 10.8783 4.03162 11.4789 4.12945 12.3884L4.3128 14.0931C4.41458 15.0393 4.49921 15.9396 4.60287 16.75H19.3971C19.5008 15.9396 19.5854 15.0393 19.6872 14.0931Z" />
                <path d="M10.9121 21H13.0879C15.9239 21 17.3418 21 18.2879 20.1532C18.7009 19.7835 18.9623 19.1172 19.151 18.25H4.84896C5.03765 19.1172 5.29913 19.7835 5.71208 20.1532C6.65817 21 8.07613 21 10.9121 21Z" />
              </svg>
              <span className={styles.wordmarkPrimary}>HENRY INTELLIGENCE</span>
            </span>
          </Link>
        </div>
        <div className={styles.topbarRight}>
          {serverOk === false && (
            <span
              className={styles.serverOfflineChip}
              title="Henry server offline"
            >
              <span className={styles.serverOfflineDot} />
              Server offline
            </span>
          )}
          <Link to="/settings" className={styles.portalsLink}>
            Settings
          </Link>
          <div className={styles.avatarWrapper} ref={avatarRef}>
            <button
              className={styles.userAvatar}
              onClick={() => setAvatarOpen((prev) => !prev)}
              title={user?.name ?? "Profile"}
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className={styles.avatarImg}
                />
              ) : uploading ? (
                "…"
              ) : (
                (user?.name?.[0]?.toUpperCase() ?? "?")
              )}
            </button>
            {avatarOpen && (
              <div className={styles.avatarPopover}>
                <button
                  className={styles.avatarPopoverItem}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {user?.picture ? "Change photo" : "Upload photo"}
                </button>
                <div className={styles.avatarPopoverDivider} />
                <a href="/auth/logout" className={styles.avatarPopoverItem}>
                  Sign out
                </a>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className={styles.avatarFileInput}
              onChange={handleAvatarFileChange}
            />
          </div>
        </div>
      </header>
      <main className={styles.shellMain}>
        <Outlet />
      </main>
      {cropFile && (
        <AvatarCropModal
          file={cropFile}
          onSave={handleCropSave}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}

export function Shell() {
  return (
    <PortalProvider>
      <ShellInner />
    </PortalProvider>
  );
}
